import { useEffect, useState, type FormEvent } from "react";

import {
  chooseSubscriptionPlan,
  listPlans,
  type UserProfile,
} from "../data/auth";

type Plan = UserProfile["subscription"]["availablePlans"][number];

const formatCardExpiry = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) {
    return digits;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const CheckoutPage = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [phone, setPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"card" | "pix">("card");
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardCpf, setCardCpf] = useState("");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly",
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
      rootHeight: root?.style.height || "",
      rootMinHeight: root?.style.minHeight || "",
    };

    html.style.overflow = "auto";
    body.style.overflow = "auto";
    html.style.height = "auto";
    body.style.height = "auto";
    if (root) {
      root.style.height = "auto";
      root.style.minHeight = "100%";
    }

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      html.style.height = previous.htmlHeight;
      body.style.height = previous.bodyHeight;
      if (root) {
        root.style.height = previous.rootHeight;
        root.style.minHeight = previous.rootMinHeight;
      }
    };
  }, []);

  useEffect(() => {
    listPlans()
      .then(({ plans: availablePlans }) => {
        setPlans(availablePlans);
        const premium = availablePlans.find(
          (plan) =>
            plan.code.toLowerCase() === "premium" ||
            plan.name.toLowerCase() === "premium",
        );
        setPlanId(premium?.id || availablePlans[0]?.id || "");
      })
      .catch(() => setError("Não foi possível carregar os planos."))
      .finally(() => setLoading(false));
  }, []);

  const selectedPlan = plans.find((plan) => plan.id === planId);
  const amount = selectedPlan
    ? billingCycle === "annual"
      ? selectedPlan.annualPriceCents
      : selectedPlan.monthlyPriceCents
    : 0;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!planId) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await chooseSubscriptionPlan(
        planId,
        billingCycle,
        // Until recurring billing is enabled, every checkout is a one-time
        // payment. The provider then offers Pix or card on its final page.
        "manual",
        email,
        {
          name,
          cpfCnpj,
          phone,
        },
      );
      if (result.checkout?.link) {
        window.location.assign(result.checkout.link);
        return;
      }
      window.location.assign("/app/checkout/success");
    } catch {
      setError("Não foi possível iniciar o checkout. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="miboard-auth-page miboard-checkout-page">
      <form className="miboard-auth-card miboard-checkout-card" onSubmit={handleSubmit}>
        <a className="miboard-checkout-back" href="/">
          ← Voltar aos planos
        </a>
        <h1>Seu carrinho</h1>
        <p className="miboard-auth-description">Preencha com seus dados.</p>
        {loading ? (
          <p>Carregando planos...</p>
        ) : (
          <>
            <div className="miboard-checkout-personal-grid">
              <label className="miboard-auth-label">
                Nome completo
                <input
                  className="miboard-auth-input"
                  id="checkout-name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Nome e sobrenome"
                  autoComplete="name"
                  required
                />
              </label>
              <label className="miboard-auth-label">
                CPF ou CNPJ
                <input
                  className="miboard-auth-input"
                  id="checkout-cpf-cnpj"
                  type="text"
                  value={cpfCnpj}
                  onChange={(event) => setCpfCnpj(event.target.value)}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  autoComplete="off"
                  required
                />
              </label>
              <label className="miboard-auth-label">
                Telefone
                <input
                  className="miboard-auth-input"
                  id="checkout-phone"
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="(11) 99999-9999"
                  autoComplete="tel"
                  required
                />
              </label>
              <label className="miboard-auth-label">
                E-mail da conta
                <input
                  className="miboard-auth-input"
                  id="checkout-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="voce@empresa.com"
                  autoComplete="email"
                  required
                />
              </label>
            </div>

            <div className="miboard-checkout-plans">
              {plans.map((plan) => (
                <label
                  key={plan.id}
                  className={`miboard-checkout-plan${
                    plan.id === planId ? " miboard-checkout-plan--selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={plan.id}
                    checked={plan.id === planId}
                    onChange={() => setPlanId(plan.id)}
                  />
                  <span>
                    <strong>{plan.name}</strong>
                    <small>{plan.description}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className="miboard-checkout-cycles">
              <button
                type="button"
                className={billingCycle === "monthly" ? "is-selected" : ""}
                onClick={() => setBillingCycle("monthly")}
              >
                Mensal
              </button>
              <button
                type="button"
                className={billingCycle === "annual" ? "is-selected" : ""}
                onClick={() => setBillingCycle("annual")}
              >
                Anual
              </button>
            </div>
            <section className="miboard-checkout-payment">
              <div className="miboard-checkout-payment-head">
                <h2>Pagamento</h2>
                <p></p>
              </div>
              <div className="miboard-checkout-methods">
                <button
                  type="button"
                  className={paymentMethod === "card" ? "is-selected" : ""}
                  onClick={() => setPaymentMethod("card")}
                >
                  Cartão
                </button>
                <button
                  type="button"
                  className={paymentMethod === "pix" ? "is-selected" : ""}
                  onClick={() => setPaymentMethod("pix")}
                >
                  Pix
                </button>
              </div>
              {paymentMethod === "card" ? (
                <div className="miboard-checkout-payment-grid">
                  <label className="miboard-auth-label">
                    Nome no cartão
                    <input
                      className="miboard-auth-input"
                      id="checkout-card-name"
                      type="text"
                      value={cardName}
                      onChange={(event) => setCardName(event.target.value)}
                      placeholder="Como aparece no cartão"
                      autoComplete="cc-name"
                      required
                    />
                  </label>
                  <label className="miboard-auth-label miboard-checkout-payment-wide">
                    Número do cartão
                    <input
                      className="miboard-auth-input"
                      id="checkout-card-number"
                      type="text"
                      value={cardNumber}
                      onChange={(event) => setCardNumber(event.target.value)}
                      placeholder="0000 0000 0000 0000"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      required
                    />
                  </label>
                  <label className="miboard-auth-label">
                    Validade
                    <input
                      className="miboard-auth-input"
                      id="checkout-card-expiry"
                      type="text"
                      value={cardExpiry}
                      onChange={(event) =>
                        setCardExpiry(formatCardExpiry(event.target.value))
                      }
                      placeholder="MM/AA"
                      inputMode="numeric"
                      maxLength={5}
                      autoComplete="cc-exp"
                      required
                    />
                  </label>
                  <label className="miboard-auth-label">
                    CVV
                    <input
                      className="miboard-auth-input"
                      id="checkout-card-cvv"
                      type="text"
                      value={cardCvv}
                      onChange={(event) => setCardCvv(event.target.value)}
                      placeholder="123"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      required
                    />
                  </label>
                  <label className="miboard-auth-label miboard-checkout-payment-wide">
                    CPF do titular
                    <input
                      className="miboard-auth-input"
                      id="checkout-card-cpf"
                      type="text"
                      value={cardCpf}
                      onChange={(event) => setCardCpf(event.target.value)}
                      placeholder="000.000.000-00"
                      inputMode="numeric"
                      autoComplete="off"
                      required
                    />
                  </label>
                </div>
              ) : (
                <div className="miboard-checkout-pix">
                  <p>
                    Ao finalizar, você será redirecionado para gerar e pagar o Pix
                    com segurança.
                  </p>
                </div>
              )}
            </section>
            <p className="miboard-checkout-total">
              Total:{" "}
              <strong>R$ {(amount / 100).toFixed(2).replace(".", ",")}</strong>/
              {billingCycle === "annual" ? "ano" : "mês"}
            </p>
            {error && <p className="miboard-auth-message">{error}</p>}
            <button
              className="miboard-auth-submit"
              type="submit"
              disabled={submitting || !planId}
            >
              {submitting ? "Redirecionando..." : "Finalizar pagamento"}
            </button>
          </>
        )}
      </form>
    </main>
  );
};
